import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { CardDelivery } from "./ui"
import { FieldsErrorType, ICardDelivery, ICardDeliveryContainer, IFieldsError } from "./types"

import { useFinality } from "@api/finality"
import { useContracts } from "@api/contracts/state"
import { IFinalityCardData, IFinalityCardFilesList, IFinalityCardListenersList, IFinalityCardTrackNumbersList, IFinalityCardLogs } from "@api/finality/state.types"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"
import { IModalPreview, ModalPreview } from "@primitive/modal.preview"

import { CardModeType } from "@smart/common.type"
import { CardDeliveryTrackNumberContainer, ICardDeliveryTrackNumberContainer } from "@smart/cardDeliveryTrackNumber"
import { CardDeliverySendPaymentContainer, ICardDeliverySendPaymentContainer } from "@smart/cardDeliverySendPayment"

import { useValue } from "@hooks/useValue"
import { validFormData } from "@hooks/useForm"
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


type DeliveryFormState = {
    deliveryToWhom: string
    deliveryAddress: string
    isAddressConfirmed: boolean
    isDeliveryCDEK: boolean
    comment: string
}

const createDeliveryFormSnapshot = (state: DeliveryFormState) => JSON.stringify(state)
const getDeliveryFormState = (card: IFinalityCardData): DeliveryFormState => ({
    deliveryToWhom: card.deliveryToWhom || '',
    deliveryAddress: card.deliveryAddress || '',
    isAddressConfirmed: card.isAddressConfirmed === 1,
    isDeliveryCDEK: card.isDeliveryCDEK === 1,
    comment: card.comment || '',
})


/** Контейнер анкеты доставки */
export const CardDeliveryContainer = ({isOpen, cardID, handlerButtonOk, handlerButtonCancel}: ICardDeliveryContainer) => {
    const notify = useContext(NotificationContext)

    const {isUploadingFile, uploadContractFiles} = useContracts(useShallow((state) => ({
        isUploadingFile: state.isLoadingCard,
        uploadContractFiles: state.actionSetContractUploadFiles
    })))
    const {
        isLoadingCard,
        getCardData, sendingOrCloseContract, sendingListenerFiles, sendingApplicationDeliveryToClient, getFiles, trackNumberSave, getTrackNumbers, saveCard
    } = useFinality(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,

        getCardData: state.actionGetFinalityCardData,
        getFiles: state.actionGetFinalityFilesList,
        getTrackNumbers: state.actionGetFinalityTrackNumbersList,

        trackNumberSave: state.actionSaveFinalityTrackNumber,
        saveCard: state.actionSaveFinalityCardData,

        sendingOrCloseContract: state.actionTelegramFinalitySendingEndCloseContract,
        sendingListenerFiles: state.actionTelegramFinalitySendingFiles,
        
        sendingApplicationDeliveryToClient: state.actionTelegramApplicationDeliveryToClient,
    })))

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const loadingTitle = useValue<string>('Загрузка анкеты...')

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalPreview = useValue<IModalPreview>({isOpen: false})
    const modalTrackNumber = useValue<ICardDeliveryTrackNumberContainer>({isOpen: false})
    const modalSendPayment = useValue<ICardDeliverySendPaymentContainer>({isOpen: false})

    const contractNumber = useValue<string>('')
    const contractDate = useValue<string>('')
    const applicationID = useValue<number | ''>('')
    const contractTypeID = useValue<number | ''>('')
    
    const txtDeliveryToWhom = useValue<string>('')
    const txtDeliveryAddress = useValue<string>('')
    const txtIsAddressConfirmed = useValue<boolean>(false)
    const txtIsDeliveryCDEK = useValue<boolean>(false)
    const txtComment = useValue<string>('')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createDeliveryFormSnapshot(getDeliveryFormState({
        contractDate: '', contractNumber: '', applicationID: 0, contractTypeID: 0,
        deliveryToWhom: '', deliveryAddress: '', isAddressConfirmed: 0, isDeliveryCDEK: 0, comment: '',
        filesList: [], listenersList: [], trackNumbersList: [], logsList: [],
    })))
    const hasSavedChangesRef = useRef(false)
    const isFormReadyRef = useRef(false)
    
    const listFiles = useValue<IFinalityCardFilesList[]>([])
    const listListeners = useValue<IFinalityCardListenersList[]>([])
    const listTrackNumbers = useValue<IFinalityCardTrackNumbersList[]>([])
    const listLogs = useValue<IFinalityCardLogs[]>([])

    const getCurrentFormSnapshot = () => createDeliveryFormSnapshot({
        deliveryToWhom: txtDeliveryToWhom.value,
        deliveryAddress: txtDeliveryAddress.value,
        isAddressConfirmed: txtIsAddressConfirmed.value,
        isDeliveryCDEK: txtIsDeliveryCDEK.value,
        comment: txtComment.value,
    })

    const applyCardData = (card: IFinalityCardData) => {
        const formState = getDeliveryFormState(card)

        contractNumber.setValue(card.contractNumber || '')
        contractDate.setValue(card.contractDate || '')
        applicationID.setValue(card.applicationID || '')
        contractTypeID.setValue(card.contractTypeID || '')
        txtDeliveryToWhom.setValue(formState.deliveryToWhom)
        txtDeliveryAddress.setValue(formState.deliveryAddress)
        txtIsAddressConfirmed.setValue(formState.isAddressConfirmed)
        txtIsDeliveryCDEK.setValue(formState.isDeliveryCDEK)
        txtComment.setValue(formState.comment)
        listFiles.setValue(card.filesList)
        listListeners.setValue(card.listenersList)
        listTrackNumbers.setValue(card.trackNumbersList)
        listLogs.setValue(card.logsList || [])

        savedFormSnapshotRef.current = createDeliveryFormSnapshot(formState)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            getCardData(Number(cardID)).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCardData(res)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = getCurrentFormSnapshot()
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        tabActive.setValue('primary')
        fieldsError.setValue({})
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createDeliveryFormSnapshot({deliveryToWhom: '', deliveryAddress: '', isAddressConfirmed: false, isDeliveryCDEK: false, comment: ''})

        contractNumber.setValue('')
        contractDate.setValue('')
        applicationID.setValue('')
        contractTypeID.setValue('')
        
        txtDeliveryToWhom.setValue('')
        txtDeliveryAddress.setValue('')
        txtIsAddressConfirmed.setValue(false)
        txtIsDeliveryCDEK.setValue(false)
        txtComment.setValue('')
        
        listFiles.setValue([])
        listListeners.setValue([])
        listTrackNumbers.setValue([])
        listLogs.setValue([])
    }

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard || isUploadingFile) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }


    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            handleButtonOk() {
                switch (actionName) {
                    case 'sendingOrCloseContract': {
                        loadingTitle.setValue('Закрываю договор, ожидайте...')

                        sendingOrCloseContract(Number(cardID), '').then((res) => {
                            if (res === 200) notify && notifyOpen('Договор успешно завершён, вся информация отправлена в telegram канал 👉 ИКТ - Доставка', 'success', 1500, notify)
                        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
                        break
                    } // Закрытие договора

                    case 'sendingApplicationDeliveryToClient': {
                        loadingTitle.setValue('Отправляю заявление на доставку, ожидайте...')

                        sendingApplicationDeliveryToClient(Number(cardID)).then((res) => {
                            if (res === 200) notify && notifyOpen('Вся информация успешно отправлена в telegram канал 👉 ИКТ - Срочно', 'success', 1500, notify)
                        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
                        break
                    } // Закрытие договора
                }

                modalConfirm.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна предпросмотра документа */
    const handlerOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }
    /** Открытие/закрытие окна создания/редактирования трек-номера */
    const handlerOpenModalTrackNumber = (isOpen: boolean, cardMode: CardModeType, deliveryID?: number, trackNumber?: string, comment?: string) => {
        modalTrackNumber.setValue({
            isOpen, cardMode, trackNumber, comment,
            handlerButtonCancel() { modalTrackNumber.setValue({ isOpen: false }) },
            handlerButtonOk(trackNumber, comment) {
                trackNumberSave(Number(cardID), Number(deliveryID ?? 0), cardMode, trackNumber, comment).then((res) => {
                    if (res === 200) notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                }).finally(() => {
                    getTrackNumbers(Number(cardID)).then((res) => {
                        listTrackNumbers.setValue(res)
                    })
                })

                modalTrackNumber.setValue({ isOpen: false })
            }
        })
    }
    /** Открытие/закрытие окна отправки счёта в telegram */
    const handleOpenModalSendPayment = (isOpen: boolean) => {
        modalSendPayment.setValue({
            isOpen, cardID, contractTypeID: contractTypeID.value || 0,
            handleBtnCancel() { modalSendPayment.setValue({ isOpen: false }) },
            handleBtnOk() {
                modalSendPayment.setValue({ isOpen: false })
            }
        })
    }


    /** Скачивание файлов */
    const handlerDownloadFiles = (fileName: string, filePath: string) => {
        const link = document.createElement('a') // Создание ссылки и программное скачивание файла

        link.href = filePath
        link.download = fileName
        document.body.appendChild(link)

        link.click()
        document.body.removeChild(link)
    }
    /** Отправка информации о трек-номере */
    const handlerSendingTrackNumbers = (trackNumber: string) => {
        loadingTitle.setValue('Отправляю информацию, ожидайте...')

        sendingOrCloseContract(Number(cardID), trackNumber).then((res) => {
            if (res === 200) notify && notifyOpen('Вся информация успешно отправлена в telegram канал 👉 ИКТ - Доставка', 'success', 1500, notify)
        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

    }
    /** Отправка информации и файлов слушателя */
    const handlerSendingListenerFiles = (contractListenerID: number) => {
        loadingTitle.setValue('Отправляю информацию, ожидайте...')

        sendingListenerFiles(Number(contractListenerID)).then((res) => {
            if (res === 200) notify && notifyOpen('Вся информация успешно отправлена в telegram канал 👉 ИКТ - Сканы дипломов', 'success', 1500, notify)
        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
    }
    // Загрузка файлов на сервер
    const handlerFileUploader = (fileList: any) => {
        loadingTitle.setValue('Отправляю файлы, ожидайте...')

        uploadContractFiles(Number(cardID), 4, fileList).then(res => {
            if (res.status === 200) {
                notify && notifyOpen('Файлы успешно загружены!', 'success', 1500, notify)
                return
            }

            notify && notifyOpen(res.message, res.status >= 500 ? 'error' : 'warning', 3000, notify)
        }).catch(error => {
            const message = error instanceof Error && error.message
                ? error.message
                : 'Не удалось загрузить файлы. Повторите попытку.'
            notify && notifyOpen(message, 'error', 3000, notify)
        }).finally(() => {
            getFiles(Number(cardID)).then((res) => {
                listFiles.setValue(res)
            })
        })
    }


    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'deliveryToWhom', value: txtDeliveryToWhom.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Необходимо заполнить поле 👉 Кому (Ф.И.О.).'},
            {fieldName: 'deliveryAddress', value: txtDeliveryAddress.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Необходимо заполнить поле 👉 Куда (Адресс доставки).'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnimation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }
        
        if (validateData.length === 0) { // Сохраняем данные
            loadingTitle.setValue('Сохранение данных...')

            saveCard(Number(cardID), txtDeliveryToWhom.value, txtDeliveryAddress.value, txtComment.value, Number(txtIsAddressConfirmed.value), Number(txtIsDeliveryCDEK.value)).then((res) => {
                if (res === 200) {
                    notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal) {
                        handlerButtonOk && handlerButtonOk()
                        return
                    }

                    savedFormSnapshotRef.current = getCurrentFormSnapshot()
                    isDisableBtnSave.setValue(true)
                    getCardData(Number(cardID)).then((card) => {
                        if (card) applyCardData(card)
                    })
                }
            }).finally(() => {
                loadingTitle.setValue('Загрузка анкеты...')
            })
        }
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnimation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)


    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardDelivery = {
        isOpen, isLoadingCard: isLoadingCard || isUploadingFile, isDisableBtnSave,
        tabActive, loadingTitle, fieldsError,
        contractNumber, contractDate, applicationID,

        txtDeliveryToWhom, txtDeliveryAddress, txtIsAddressConfirmed, txtIsDeliveryCDEK,
        txtComment, listFiles, listListeners, listTrackNumbers, listLogs,

        handlerOpenModalConfirm, handlerOpenModalPreview, handlerDownloadFiles, 
        handlerSendingTrackNumbers, handlerSendingListenerFiles, handlerFileUploader,
        handlerOpenModalTrackNumber, handleBtnSave, handleBtnClose, handleOpenModalSendPayment
    }
    
    return <>
        <CardDelivery {...propsToComponent} />
        
        <ModalConfirm {...modalConfirm.value} />
        <ModalPreview {...modalPreview.value} />
        <CardDeliveryTrackNumberContainer {...modalTrackNumber.value} />
        <CardDeliverySendPaymentContainer {...modalSendPayment.value} />
    </>
}